"use client";

import { useEffect, useState } from 'react';
import { useLocale } from 'next-intl';
import { useRouter } from 'next/navigation';
import styles from './page.module.css';

export default function ProfilePage() {
  const [user, setUser] = useState<any>(null);
  const [activeTab, setActiveTab] = useState('DASHBOARD');
  const [pointsHistory, setPointsHistory] = useState<any[]>([]);
  const [referrals, setReferrals] = useState<any[]>([]);
  const [projects, setProjects] = useState<any[]>([]);
  const locale = useLocale();
  const isZh = locale === 'zh';
  const router = useRouter();

  const resolveImg = (url: string) => {
    if (!url) return '';
    // Strip any explicit domain so we can rely on relative proxy mapping
    url = url.replace(/http:\/\/(127\.0\.0\.1|localhost|44\.212\.99\.135):5000/g, '');
    if (url.startsWith('http')) return url;
    return url.startsWith('/') ? url : `/${url}`;
  };

  useEffect(() => {
    const fetchMe = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        router.push(`/${locale}/login`);
        return;
      }

      try {
        const res = await fetch('/api/auth/me', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await res.json();
        if (res.ok) {
          setUser(data);
        } else {
          localStorage.removeItem('token');
          router.push(`/${locale}/login`);
        }
      } catch (err) {
        console.error(err);
      }
    };
    fetchMe();
  }, [locale, router]);

  const loadPoints = async () => {
    setActiveTab('POINTS');
    const token = localStorage.getItem('token');
    try {
      const res = await fetch('/api/auth/me/points', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setPointsHistory(await res.json());
    } catch (err) { }
  };

  const loadReferrals = async () => {
    setActiveTab('REFERRALS');
    const token = localStorage.getItem('token');
    try {
      const res = await fetch('/api/auth/me/referrals', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setReferrals(await res.json());
    } catch (err) { }
  };

  const loadProjects = async (tabName = 'PROJECTS') => {
    setActiveTab(tabName);
    const token = localStorage.getItem('token');
    try {
      const res = await fetch('/api/project', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setProjects(await res.json());
    } catch (err) { }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    // After logging out, go to the homepage instead of the login page
    router.push(`/${locale}`);
    router.refresh();
  };

  if (!user) return <div className="container" style={{ padding: '160px 0', textAlign: 'center' }}>{isZh ? '加载中...' : 'Loading...'}</div>;

  return (
    <div className={styles.profileContainer} style={{ paddingTop: '120px' }}>
      <aside className={styles.sidebar}>
        <div className={styles.userInfo}>
          <div className={styles.avatar}>
            {user.name ? user.name.charAt(0) : 'U'}
          </div>
          <h3>{user.name || user.email.split('@')[0]}</h3>
          <p className={styles.roleText}>
            {user.role === 'ADMIN' ? (isZh ? '管理员' : 'Admin') : (isZh ? '认证账户' : 'Verified Account')}
          </p>
        </div>
        <nav className={styles.sideNav}>
          <button className={activeTab === 'DASHBOARD' ? styles.active : ''} onClick={() => setActiveTab('DASHBOARD')}>
            {isZh ? '个人主页' : 'Dashboard'}
          </button>
          <button className={activeTab === 'POINTS' ? styles.active : ''} onClick={loadPoints}>
            {isZh ? '我的积分' : 'My Points'}
          </button>
          <button className={activeTab === 'REFERRALS' ? styles.active : ''} onClick={loadReferrals}>
            {isZh ? '我的推荐' : 'My Referrals'}
          </button>
          <button className={activeTab === 'CERTIFICATION' ? styles.active : ''} onClick={() => loadProjects('CERTIFICATION')}>
            {isZh ? '我的认证' : 'My Certification'}
          </button>
          <button className={activeTab === 'PROJECTS' ? styles.active : ''} onClick={() => loadProjects('PROJECTS')}>
            {isZh ? '数字证书库' : 'Digital Certificates'}
          </button>
          <button className={styles.applyBtnSidebar} onClick={() => router.push(`/${locale}/apply`)}>
            {isZh ? '认证申请' : 'Apply Cert'}
          </button>
          <button className={styles.logoutBtn} onClick={handleLogout}>
            {isZh ? '退出登录' : 'Logout'}
          </button>
        </nav>
      </aside>

      <main className={styles.mainContent} style={{ maxWidth: '1200px' }}>
        {activeTab === 'DASHBOARD' && (
          <>
            <h2>{isZh ? '欢迎来到个人工作台' : 'Welcome to your Dashboard'}</h2>

            <div className={styles.statsGrid}>
              <div className={styles.statCard}>
                <h4>{isZh ? '当前积分' : 'Current Points'}</h4>
                <p className={styles.statValue}>{user.points}</p>
              </div>
              <div className={styles.statCard}>
                <h4>{isZh ? '推荐下线人数' : 'Referral Count'}</h4>
                <p className={styles.statValue}>{user.referralCount || 0}</p>
              </div>
              <div className={styles.statCard}>
                <h4>{isZh ? '累计成长积分' : 'Lifetime Points'}</h4>
                <p className={styles.statValue}>{user.lifetimePoints || user.points}</p>
              </div>
            </div>

            <div className={styles.card}>
              <h3>{isZh ? '推广专属链接' : 'Invite Link'}</h3>
              <p>{isZh ? '您的永久推荐邀请码为：' : 'Your permanent invite code: '}<strong>{user.referralCode}</strong></p>
              <div className={styles.inviteBox}>
                <input type="text" readOnly value={`http://44.212.99.135/${locale}/register?invite=${user.referralCode}`} />
                <button onClick={() => navigator.clipboard.writeText(`http://44.212.99.135/${locale}/register?invite=${user.referralCode}`)}>
                  {isZh ? '复制链接' : 'Copy Link'}
                </button>
              </div>
            </div>
          </>
        )}

        {activeTab === 'POINTS' && (
          <div className={styles.card}>
            <h3>{isZh ? '积分流水' : 'Points History'}</h3>
            <table style={{ width: '100%', marginTop: '20px', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f8f9fa', borderBottom: '2px solid #eee' }}>
                  <th style={{ padding: '12px', textAlign: 'left' }}>{isZh ? '变动数量' : 'Amount'}</th>
                  <th style={{ padding: '12px', textAlign: 'left' }}>{isZh ? '变动原因' : 'Reason'}</th>
                  <th style={{ padding: '12px', textAlign: 'left' }}>{isZh ? '时间' : 'Time'}</th>
                </tr>
              </thead>
              <tbody>
                {pointsHistory.map((h, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: '12px', color: h.type === 'ADD' ? 'green' : 'red', fontWeight: 'bold' }}>
                      {h.type === 'ADD' ? '+' : '-'}{h.amount}
                    </td>
                    <td style={{ padding: '12px', color: '#555' }}>{h.reason}</td>
                    <td style={{ padding: '12px', color: '#999' }}>{new Date(h.createdAt).toLocaleString()}</td>
                  </tr>
                ))}
                {pointsHistory.length === 0 && <tr><td colSpan={3} style={{ textAlign: 'center', padding: '20px' }}>{isZh ? '暂无记录' : 'No records'}</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === 'REFERRALS' && (
          <div className={styles.card}>
            <h3>{isZh ? '我推荐的成员 (一级)' : 'My Direct Referrals'}</h3>
            <table style={{ width: '100%', marginTop: '20px', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f8f9fa', borderBottom: '2px solid #eee' }}>
                  <th style={{ padding: '12px', textAlign: 'left' }}>{isZh ? '账户邮箱' : 'Email'}</th>
                  <th style={{ padding: '12px', textAlign: 'left' }}>{isZh ? '姓名' : 'Name'}</th>
                  <th style={{ padding: '12px', textAlign: 'left' }}>{isZh ? '注册时间' : 'Join Date'}</th>
                </tr>
              </thead>
              <tbody>
                {referrals.map((r, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: '12px', color: '#555' }}>{r.email}</td>
                    <td style={{ padding: '12px', color: '#555' }}>{r.name || (isZh ? '未填写' : 'N/A')}</td>
                    <td style={{ padding: '12px', color: '#999' }}>{new Date(r.createdAt).toLocaleDateString()}</td>
                  </tr>
                ))}
                {referrals.length === 0 && <tr><td colSpan={3} style={{ textAlign: 'center', padding: '20px' }}>{isZh ? '暂无成员' : 'No referrals yet'}</td></tr>}
              </tbody>
            </table>
          </div>
        )}
        {activeTab === 'CERTIFICATION' && (
          <div className={styles.card}>
            <h3>{isZh ? '我的认证状态' : 'My Certification Status'}</h3>
            <div style={{ marginTop: '20px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {projects.map((p, i) => (
                <div key={i} style={{ border: '1px solid #eee', borderRadius: '8px', padding: '20px', background: '#fff' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '15px', alignItems: 'center' }}>
                    <h4 style={{ margin: 0, color: '#333', fontSize: '18px' }}>
                      {p.inheritor} 
                      <span style={{ fontSize: '14px', color: '#666', fontWeight: 'normal', marginLeft: '10px' }}>
                        ({p.certType === 'INDIVIDUAL' ? (isZh ? '传承人' : 'Individual') : p.certType === 'PROJECT' ? (isZh ? '非遗项目' : 'Project') : (isZh ? '机构' : 'Organization')})
                      </span>
                    </h4>
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <span style={{ 
                        padding: '4px 12px', 
                        borderRadius: '20px', 
                        fontSize: '12px',
                        background: p.status === 'APPROVED' ? '#4ade80' : p.status === 'REJECTED' ? '#f87171' : '#fcd34d',
                        color: p.status === 'PENDING' ? '#000' : '#fff'
                      }}>
                        {isZh ? (p.status === 'APPROVED' ? '已通过' : p.status === 'REJECTED' ? '已拒绝' : '审核中') : p.status}
                      </span>
                      {p.status === 'PENDING' && (
                        <span style={{ padding: '4px 12px', borderRadius: '20px', fontSize: '12px', background: '#e5e7eb', color: '#374151' }}>
                          {p.reviewStage === 'INITIAL' ? (isZh ? '初审中' : 'Initial Review') : (isZh ? '人工复核中' : 'Manual Review')}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className={styles.certDetails} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
                    <div>
                      <label style={{ color: '#999', fontSize: '12px', display: 'block' }}>{isZh ? '认证级别' : 'Level'}</label>
                      <div style={{ display: 'flex', alignItems: 'center', marginTop: '5px' }}>
                        <span style={{ marginRight: '10px' }}>
                          {p.category === 'CITY' ? (isZh ? '市县级' : 'City/County') : 
                           p.category === 'PROVINCE' ? (isZh ? '省级' : 'Provincial') : 
                           (isZh ? '世界级' : 'World/International')}
                        </span>
                        <div style={{ display: 'flex', color: '#3b82f6' }}>
                          {Array.from({ length: p.diamonds || 1 }).map((_, idx) => (
                            <span key={idx} style={{ fontSize: '18px' }}>♦</span>
                          ))}
                        </div>
                      </div>
                    </div>

                    {p.status === 'APPROVED' && (
                      <div>
                        <label style={{ color: '#999', fontSize: '12px', display: 'block' }}>{isZh ? '评级结果' : 'Grading'}</label>
                        <div style={{ marginTop: '5px' }}>
                          {p.gradingLevel === 'LEVEL1' && <span style={{ color: '#b87333', fontWeight: 'bold' }}>{isZh ? 'Level 1: 基础认证 (铜色)' : 'Level 1: Basic (Bronze)'}</span>}
                          {p.gradingLevel === 'LEVEL2' && <span style={{ color: '#c0c0c0', fontWeight: 'bold' }}>{isZh ? 'Level 2: 优秀认证 (银色)' : 'Level 2: Excellent (Silver)'}</span>}
                          {p.gradingLevel === 'LEVEL3' && <span style={{ color: '#ffd700', fontWeight: 'bold' }}>{isZh ? 'Level 3: 推荐国际评选 (金色)' : 'Level 3: International Recommended (Gold)'}</span>}
                          {!p.gradingLevel && <span style={{ color: '#999' }}>{isZh ? '暂未评级' : 'Not Graded'}</span>}
                        </div>
                      </div>
                    )}

                    <div>
                      <label style={{ color: '#999', fontSize: '12px', display: 'block' }}>{isZh ? '提交日期' : 'Submitted Date'}</label>
                      <div style={{ marginTop: '5px' }}>{new Date(p.createdAt).toLocaleDateString()}</div>
                    </div>

                    {p.certNo && (
                      <div>
                        <label style={{ color: '#999', fontSize: '12px', display: 'block' }}>{isZh ? '证书编号' : 'Certificate No.'}</label>
                        <div style={{ marginTop: '5px', fontFamily: 'monospace', fontWeight: 'bold' }}>{p.certNo}</div>
                      </div>
                    )}
                  </div>

                  {p.adminMemo && (
                    <div style={{ marginTop: '15px', padding: '10px', background: '#fff7ed', borderRadius: '4px', fontSize: '14px', borderLeft: '4px solid #f97316' }}>
                      <strong>{isZh ? '审核意见：' : 'Review Note: '}</strong> {p.adminMemo}
                    </div>
                  )}
                </div>
              ))}
              {projects.length === 0 && (
                <div style={{ textAlign: 'center', padding: '60px 40px', color: '#999', background: '#f9fafb', borderRadius: '8px', border: '2px dashed #e5e7eb' }}>
                  <p style={{ marginBottom: '20px' }}>{isZh ? '您尚未提交任何认证申请' : 'You have not submitted any certification applications yet'}</p>
                  <button 
                    onClick={() => router.push(`/${locale}/apply`)}
                    style={{ padding: '10px 30px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}
                  >
                    {isZh ? '立即申请认证' : 'Apply Now'}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'PROJECTS' && (
          <div className={styles.card}>
            <h3>{isZh ? '数字证书库' : 'Digital Certificates'}</h3>
            <div style={{ marginTop: '20px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {projects.filter(p => p.status === 'APPROVED').map((p, i) => (
                <div key={i} style={{ border: '1px solid #eee', borderRadius: '8px', padding: '20px', background: '#f0fdf4' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '15px' }}>
                    <h4 style={{ margin: 0, color: '#333' }}>{p.inheritor} - {isZh ? '正式证书' : 'Official Certificate'}</h4>
                    <span style={{ fontSize: '12px', color: '#15803d', fontWeight: 'bold' }}>{p.certNo}</span>
                  </div>
                  
                  {p.certFileUrl && (
                    <div style={{ marginTop: '10px' }}>
                      <div style={{ height: '500px', overflow: 'hidden', border: '1px solid #ddd', borderRadius: '4px' }}>
                        {p.certFileUrl.toLowerCase().endsWith('.pdf') ? (
                          <iframe src={resolveImg(p.certFileUrl) + '#toolbar=0'} style={{ width: '100%', height: '100%', border: 'none' }} />
                        ) : (
                          <img src={resolveImg(p.certFileUrl)} style={{ width: '100%', height: '100%', objectFit: 'contain', background: '#fafafa' }} />
                        )}
                      </div>
                      <div style={{ marginTop: '15px', textAlign: 'center' }}>
                        <a href={resolveImg(p.certFileUrl)} target="_blank" rel="noreferrer" style={{ display: 'inline-block', padding: '10px 30px', background: '#2563eb', color: '#fff', textDecoration: 'none', borderRadius: '4px', fontSize: '14px', fontWeight: 'bold' }}>
                          {isZh ? '下载高清证书原件' : 'Download HD Certificate'}
                        </a>
                      </div>
                    </div>
                  )}
                  {!p.certFileUrl && (
                    <p style={{ marginTop: '10px', color: '#2563eb', fontSize: '14px' }}>{isZh ? '✅ 证书正在制发中或已由线下颁发。' : '✅ Certificate is being processed.'}</p>
                  )}
                </div>
              ))}
              {projects.filter(p => p.status === 'APPROVED').length === 0 && <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>{isZh ? '暂无已获证证书' : 'No certificates issued yet'}</div>}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
